import { Bell, CheckCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMarkRead, useNotifications } from "../api/budgets.js";
import { Link } from "@tanstack/react-router";
import { approvalOf, NotificationRow } from "../app/notifications.js";
import { ErrorMessage } from "../ui/feedback.js";
import { EmptyState, PageHeader } from "../ui/layout.js";
import { Button, Card, Spinner } from "../ui/primitives.js";

/** Everything the bell has: the person's last 50 notifications (docs/budgets.md). */
export function NotificationsPage() {
  const { t } = useTranslation();
  const list = useNotifications();
  const read = useMarkRead();
  const unread = list.data?.unread ?? 0;
  return (
    <>
      <PageHeader
        title={t("notifications.title")}
        intro={t("notifications.intro")}
        help="costs"
        actions={
          unread > 0 ? (
            <Button
              variant="secondary"
              icon={<CheckCheck className="size-4" aria-hidden />}
              busy={read.isPending}
              onClick={() => read.mutate(undefined)}
            >
              {t("notifications.markAll")}
            </Button>
          ) : undefined
        }
      />
      {list.error ? (
        <ErrorMessage error={list.error} />
      ) : !list.data ? (
        <Spinner label={t("common.loading")} />
      ) : list.data.items.length === 0 ? (
        <EmptyState
          icon={<Bell className="size-6" aria-hidden />}
          title={t("notifications.empty")}
          help="costs"
        >
          {t("notifications.emptyLead")}
        </EmptyState>
      ) : (
        <Card className="py-1">
          <ul className="divide-y divide-line">
            {list.data.items.map((n) => (
              <li key={n.id} className="flex items-start justify-between gap-3 py-3">
                <NotificationRow n={n} />
                {approvalOf(n) && (
                  <Link
                    to="/api-access/approvals/$approvalId"
                    params={{ approvalId: approvalOf(n) ?? "" }}
                    onClick={() => !n.readAt && read.mutate([n.id])}
                    className="shrink-0 rounded-lg px-3 py-2 text-sm text-accent hover:bg-surface-2"
                  >
                    {t("apiAccess.approvals.openItem")}
                  </Link>
                )}
                {!n.readAt && (
                  <Button variant="ghost" onClick={() => read.mutate([n.id])} className="shrink-0">
                    {t("notifications.markRead")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
